import PlaceIcon from '@mui/icons-material/Place';
import { Box, Stack, Typography } from '@mui/material';
import { differenceInMinutes } from 'date-fns';
import { LngLatLike } from 'mapbox-gl';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useDeviceTrack } from '@/api/device/useDeviceTrack';
import { useDevice, useDeviceLatestData } from '@/api/devices';
import { LatLong } from '@/components/data/LatLong';
import { SensorIcon } from '@/components/data/SensorIcon';
import { Timestamp, TimestampRange } from '@/components/data/Timestamp';
import { MapBaseLayerToggle, MapControlsStack } from '@/components/map/LayerControls';
import { SafeMap } from '@/components/map/SafeMap';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useMapUiContext } from '@/context/MapUiContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useElementSize } from '@/hooks/useElementSize';
import { useTzContext } from '@/hooks/useTzContext';
import { minorTextGrey } from '@/theme';
import { findClosestByBinarySearch, isInRange, isScoutDevice, spotterToLngLat } from '@/utils';

import { DEFAULT_GEOLOCATION, ScaleControl } from '../../map/SpotterMap';
import { GeofenceDisplayLayer } from '../../map/layers/GeofenceDisplayLayer';
import { HistoricalSpotterTrackLayer } from '../../map/layers/SpotterTrackLayer';

export const SpotterTrackMap = () => {
    const tz = useTzContext();
    const { weatherLayers } = useMapUiContext();
    const { spotterId, viaOceansApi } = useSpotterContext();
    const [initialLocation, setInitialLocation] = useState<LngLatLike>();
    const { dateRange } = useDateRangeContext();
    const { selectedTimestamp } = useTimelineNavigationContext();
    const mapContainerRef = useRef<HTMLElement>(null);
    const { width } = useElementSize(mapContainerRef, { responsive: true });

    const { data: device } = useDevice({ config: { enabled: !viaOceansApi } });
    const showGeofence = !isScoutDevice(device?.hullType);
    const { device: deviceLatestData, isPending: isLatestDataPending } = useDeviceLatestData();
    const { data: deviceTrackHistory } = useDeviceTrack({
        dateRange,
    });

    const track = useMemo(
        () =>
            deviceTrackHistory?.length
                ? deviceTrackHistory
                : deviceLatestData?.track?.length
                  ? [deviceLatestData.track[0]]
                  : [],
        [deviceTrackHistory, deviceLatestData]
    );

    const selectedTrackItem = useMemo(() => {
        if (!track?.length || !selectedTimestamp) {
            return undefined;
        }

        const selectedTime = new Date(selectedTimestamp).getTime();

        const closestTs = findClosestByBinarySearch(track, selectedTime, (track) =>
            new Date(track.timestamp).getTime()
        );

        if (
            closestTs &&
            Math.abs(differenceInMinutes(selectedTimestamp, closestTs.timestamp)) <=
                (device?.currentMessagePeriod ?? 1) * 60
        ) {
            return closestTs;
        }

        return undefined;
    }, [track, selectedTimestamp, device?.currentMessagePeriod]);

    useEffect(() => {
        // Set initial map center
        if (initialLocation || isLatestDataPending || !track.length) {
            return;
        }

        if (track?.[0]) {
            setInitialLocation(spotterToLngLat(track[0]));
        } else {
            setInitialLocation(spotterToLngLat(DEFAULT_GEOLOCATION));
        }
    }, [isLatestDataPending, track, initialLocation, spotterId]);

    return (
        <Stack sx={{ height: 1, gap: 0.5, containerType: 'inline-size' }}>
            <Stack
                sx={{
                    gap: 0.5,
                    display: { '@150': 'flex', '@': 'none' },
                }}
            >
                <SectionHeader
                    title="Spotter location"
                    icon={<SensorIcon Icon={PlaceIcon} />}
                    isLoading={isLatestDataPending}
                    sx={{ width: 1, flexWrap: 'wrap' }}
                ></SectionHeader>
                {selectedTrackItem ? (
                    <>
                        <Timestamp
                            variant="caption"
                            value={selectedTrackItem?.timestamp}
                            type="short"
                            sx={{ color: minorTextGrey }}
                        />
                        <LatLong coord={selectedTrackItem} options={{ showSecs: true }} />
                    </>
                ) : (
                    <>
                        {dateRange && track?.[0] && isInRange(new Date(track[0].timestamp), dateRange, { tz }) && (
                            <TimestampRange dateRange={dateRange} variant="caption" sx={{ color: minorTextGrey }} />
                        )}
                        <Typography>
                            <em>No location selected</em>
                        </Typography>
                    </>
                )}
            </Stack>
            <Box ref={mapContainerRef} sx={{ aspectRatio: 1, width: 1 }}>
                <SafeMap
                    mapType="historicalTrack"
                    showWeather={false}
                    allowMapZoomOnScroll
                    weatherLayers={weatherLayers}
                    center={initialLocation}
                    zoom={15}
                >
                    {showGeofence && <GeofenceDisplayLayer layerId="spotter-track-map" device={device} />}
                    <HistoricalSpotterTrackLayer track={track} selectedTrackItem={selectedTrackItem} />
                    {width >= 150 && (
                        <>
                            <ScaleControl />
                            <MapControlsStack sx={{ right: '4px', bottom: '4px' }}>
                                <MapBaseLayerToggle size="1.5em" sx={{ p: '2px' }} />
                            </MapControlsStack>
                        </>
                    )}
                </SafeMap>
            </Box>
        </Stack>
    );
};
